import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, CircleAlert } from 'lucide-react';
import { useToast } from './ToastProvider';
import { pagosAPI, describirError } from '../services/api';
import { formatoMoneda } from '../utils/finanzas';
import '../styles/FormModal.css';
import '../styles/Pages.css';

const fechaLarga = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

/**
 * "Reversar pago" (P08-B0). Reutiliza el endpoint ya certificado
 * (POST /pagos/:id/reversar, P07/P07.5): el frontend solo pide la
 * autorización; el backend vuelve a validar permiso (PAGOS_REVERSAR), estado
 * del pago y si está en un cierre de caja cerrado. Nunca borra el pago.
 *
 * @param {object} props.pago - pago serializado por la API (folioPago, fechaPago, importeRecibido, formaPago).
 * @param {string} [props.folioCredito] - folio del crédito, si el pago no lo trae.
 */
function ReversarPagoModal({ pago, folioCredito, onClose, onReversado }) {
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const motivoValido = motivo.trim().length >= 3;
  const dialogoRef = useRef(null);
  // Al cerrar, el foco vuelve al control que abrió el diálogo (se captura antes del autoFocus del motivo).
  const abridorRef = useRef(document.activeElement);
  const cerrar = useCallback(() => {
    onClose();
    const abridor = abridorRef.current;
    setTimeout(() => {
      if (abridor && typeof abridor.focus === 'function' && document.contains(abridor)) abridor.focus();
    }, 0);
  }, [onClose]);

  // Teclado: Escape cierra (salvo mientras se envía) y el foco no sale del diálogo.
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === 'Escape' && !enviando) {
        cerrar();
        return;
      }
      if (e.key !== 'Tab' || !dialogoRef.current) return;
      const f = [...dialogoRef.current.querySelectorAll('button, textarea, input, select, [href], [tabindex]:not([tabindex="-1"])')]
        .filter((el) => !el.disabled && el.offsetParent !== null);
      if (f.length === 0) return;
      const primero = f[0];
      const ultimo = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === primero || !dialogoRef.current.contains(document.activeElement))) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (document.activeElement === ultimo || !dialogoRef.current.contains(document.activeElement))) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [enviando, cerrar]);

  const enviar = async (e) => {
    e.preventDefault();
    if (!motivoValido || enviando) return;
    setEnviando(true);
    try {
      await pagosAPI.reversar(pago.id, motivo.trim(), observaciones.trim() || undefined);
      await onReversado();
    } catch (e2) {
      toast.error(describirError(e2).mensaje);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={cerrar}>
      <div className="modal-content" ref={dialogoRef} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="reversar-titulo">
        <div className="modal-header">
          <h2 id="reversar-titulo">Reversar pago {pago.folioPago}</h2>
          <button type="button" className="modal-close" onClick={cerrar} aria-label="Cerrar">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <form className="modal-form" onSubmit={enviar}>
          <dl className="pagos-detalle-lista">
            <div><dt>Folio</dt><dd>{pago.folioPago}</dd></div>
            <div><dt>Fecha</dt><dd>{fechaLarga(pago.fechaPago)}</dd></div>
            <div><dt>Importe</dt><dd>{formatoMoneda(pago.importeRecibido)}</dd></div>
            <div><dt>Forma de pago</dt><dd>{pago.formaPago}</dd></div>
            <div><dt>Crédito</dt><dd>{pago.credito?.folio || folioCredito || '—'}</dd></div>
          </dl>

          <div className="data-notice is-error">
            <CircleAlert size={16} strokeWidth={1.75} />
            <span>
              Esta operación restablecerá los saldos del crédito.
              El pago permanecerá en el historial como REVERSADO.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="reversa-motivo">Motivo (obligatorio)</label>
            <textarea id="reversa-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} required minLength={3} maxLength={500} autoFocus />
          </div>

          <div className="form-group">
            <label htmlFor="reversa-observaciones">Observaciones (opcional)</label>
            <textarea id="reversa-observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} maxLength={400} />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={cerrar}>Cancelar</button>
            <button type="submit" className="btn btn-danger" disabled={!motivoValido || enviando}>
              {enviando ? 'Reversando…' : 'Autorizar reversa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReversarPagoModal;
